import React, { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import { formatPhone } from '../../utils/phone';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://app.titanelectricreno.com:8080/api';

function getToken() {
  try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.token; }
  catch { return null; }
}

async function apiFetch(path, opts = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { 'x-worker-token': getToken(), 'Content-Type': 'application/json', ...(opts.headers || {}) },
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

const EMPTY = { vendor_no: '', name: '', address: '', city: '', state: '', zip: '', phone: '', fax: '', contact: '', email: '', terms: '', notes: '', status: 'active' };

function VendorModal({ vendor, onClose, onSave }) {
  const [form, setForm] = useState(vendor ? { ...vendor } : { ...EMPTY });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) return setErr('Name is required');
    setSaving(true); setErr('');
    try {
      const saved = vendor
        ? await apiFetch(`/vendors/${vendor.id}`, { method: 'PATCH', body: JSON.stringify(form) })
        : await apiFetch('/vendors', { method: 'POST', body: JSON.stringify(form) });
      onSave(saved);
    } catch (ex) { setErr(ex.message); } finally { setSaving(false); }
  }

  const field = (label, key, opts = {}) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</label>
      <input
        value={form[key] || ''}
        onChange={e => set(key, e.target.value)}
        placeholder={opts.placeholder || ''}
        style={{ border: '1px solid #e5e7eb', borderRadius: 7, padding: '8px 10px', fontSize: 13, outline: 'none' }}
      />
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 14, width: 580, maxWidth: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>{vendor ? 'Edit Vendor' : 'Add Vendor'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#9ca3af' }}>×</button>
        </div>
        <form onSubmit={handleSubmit} style={{ overflowY: 'auto', flex: 1, padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
            {field('Vendor #', 'vendor_no')}
            {field('Company Name *', 'name')}
          </div>
          {field('Address', 'address')}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12 }}>
            {field('City', 'city')}
            {field('State', 'state')}
            {field('ZIP', 'zip')}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {field('Phone', 'phone')}
            {field('Fax', 'fax')}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {field('Contact', 'contact')}
            {field('Email', 'email')}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {field('Payment Terms', 'terms', { placeholder: 'NET30' })}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>STATUS</label>
              <select value={form.status} onChange={e => set('status', e.target.value)}
                style={{ border: '1px solid #e5e7eb', borderRadius: 7, padding: '8px 10px', fontSize: 13 }}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>NOTES</label>
            <textarea value={form.notes || ''} onChange={e => set('notes', e.target.value)} rows={2}
              style={{ border: '1px solid #e5e7eb', borderRadius: 7, padding: '8px 10px', fontSize: 13, resize: 'vertical' }} />
          </div>
          {err && <p style={{ color: '#dc2626', fontSize: 13, margin: 0 }}>{err}</p>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 4 }}>
            <button type="button" onClick={onClose} style={{ padding: '9px 20px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>Cancel</button>
            <button type="submit" disabled={saving} style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: '#f97316', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              {saving ? 'Saving…' : 'Save Vendor'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminVendors() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [showModal, setShowModal] = useState(false);
  const [editVendor, setEditVendor] = useState(null);

  useEffect(() => {
    apiFetch('/vendors').then(setVendors).finally(() => setLoading(false));
  }, []);

  async function handleDelete(id) {
    if (!window.confirm('Delete this vendor?')) return;
    await apiFetch(`/vendors/${id}`, { method: 'DELETE' });
    setVendors(prev => prev.filter(v => v.id !== id));
  }

  const filtered = vendors.filter(v => {
    const matchStatus = statusFilter === 'all' || v.status === statusFilter;
    const q = search.toLowerCase();
    const matchSearch = !q || v.name.toLowerCase().includes(q) || (v.vendor_no || '').includes(q) || (v.city || '').toLowerCase().includes(q) || (v.contact || '').toLowerCase().includes(q);
    return matchStatus && matchSearch;
  });

  return (
    <AdminLayout>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Vendors</h1>
          <p style={{ color: '#6b7280', margin: '4px 0 0', fontSize: 14 }}>{vendors.filter(v => v.status === 'active').length} active vendors</p>
        </div>
        <button onClick={() => { setEditVendor(null); setShowModal(true); }}
          style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: '#f97316', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
          + Add Vendor
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, number, city…"
          style={{ flex: 1, minWidth: 220, border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 14px', fontSize: 13 }}
        />
        <div style={{ display: 'flex', background: '#f3f4f6', borderRadius: 8, padding: 3, gap: 2 }}>
          {[['active', 'Active'], ['inactive', 'Inactive'], ['all', 'All']].map(([val, label]) => (
            <button key={val} onClick={() => setStatusFilter(val)}
              style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: statusFilter === val ? '#fff' : 'transparent', fontWeight: 600, fontSize: 12, cursor: 'pointer', color: statusFilter === val ? '#374151' : '#6b7280', boxShadow: statusFilter === val ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {loading ? <p style={{ color: '#9ca3af' }}>Loading…</p> : (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                <th style={{ textAlign: 'left', padding: '10px 16px', fontSize: 11, fontWeight: 700, color: '#6b7280', letterSpacing: 0.5 }}>VENDOR #</th>
                <th style={{ textAlign: 'left', padding: '10px 16px', fontSize: 11, fontWeight: 700, color: '#6b7280', letterSpacing: 0.5 }}>NAME</th>
                <th style={{ textAlign: 'left', padding: '10px 16px', fontSize: 11, fontWeight: 700, color: '#6b7280', letterSpacing: 0.5 }}>LOCATION</th>
                <th style={{ textAlign: 'left', padding: '10px 16px', fontSize: 11, fontWeight: 700, color: '#6b7280', letterSpacing: 0.5 }}>PHONE</th>
                <th style={{ textAlign: 'left', padding: '10px 16px', fontSize: 11, fontWeight: 700, color: '#6b7280', letterSpacing: 0.5 }}>CONTACT</th>
                <th style={{ textAlign: 'left', padding: '10px 16px', fontSize: 11, fontWeight: 700, color: '#6b7280', letterSpacing: 0.5 }}>TERMS</th>
                <th style={{ textAlign: 'left', padding: '10px 16px', fontSize: 11, fontWeight: 700, color: '#6b7280', letterSpacing: 0.5 }}>STATUS</th>
                <th style={{ padding: '10px 16px' }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={8} style={{ padding: '32px 16px', textAlign: 'center', color: '#9ca3af' }}>No vendors found.</td></tr>
              )}
              {filtered.map((v, i) => (
                <tr key={v.id} style={{ borderTop: i === 0 ? 'none' : '1px solid #f3f4f6' }}>
                  <td style={{ padding: '12px 16px', color: '#6b7280', fontFamily: 'monospace', fontSize: 12 }}>{v.vendor_no}</td>
                  <td style={{ padding: '12px 16px', fontWeight: 600 }}>
                    {v.name}
                    {v.notes && <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 400, marginTop: 2 }}>{v.notes}</div>}
                  </td>
                  <td style={{ padding: '12px 16px', color: '#6b7280' }}>
                    {[v.city, v.state].filter(Boolean).join(', ')}
                  </td>
                  <td style={{ padding: '12px 16px', color: '#374151' }}>{formatPhone(v.phone)}</td>
                  <td style={{ padding: '12px 16px', color: '#6b7280' }}>
                    {v.contact && <div>{v.contact}</div>}
                    {v.email && <div style={{ fontSize: 11 }}>{v.email}</div>}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {v.terms && <span style={{ background: '#f3f4f6', color: '#374151', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99 }}>{v.terms}</span>}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ background: v.status === 'active' ? '#dcfce7' : '#f3f4f6', color: v.status === 'active' ? '#16a34a' : '#6b7280', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99, textTransform: 'capitalize' }}>
                      {v.status}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                    <button onClick={() => { setEditVendor(v); setShowModal(true); }}
                      style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', marginRight: 6 }}>
                      Edit
                    </button>
                    <button onClick={() => handleDelete(v.id)}
                      style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid #fecaca', background: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#dc2626' }}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length > 0 && (
            <div style={{ padding: '10px 16px', borderTop: '1px solid #f3f4f6', fontSize: 12, color: '#9ca3af' }}>
              Showing {filtered.length} of {vendors.length} vendors
            </div>
          )}
        </div>
      )}

      {showModal && (
        <VendorModal
          vendor={editVendor}
          onClose={() => { setShowModal(false); setEditVendor(null); }}
          onSave={saved => {
            setVendors(prev => editVendor ? prev.map(v => v.id === saved.id ? saved : v) : [saved, ...prev]);
            setShowModal(false); setEditVendor(null);
          }}
        />
      )}
    </AdminLayout>
  );
}
